import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { routes, URL_VARS } from '../../../constants/routing';
import { addInventoryItemValue } from '../../../store/actions/inventories';
import { selectIsSuperAdmin } from '../../../store/selectors/auth';
import { IInventoryItem } from '../../../types/inventories';

interface IProps {
  onEdit?: (item: IInventoryItem) => void;
  onDelete?: (itemId: string) => void;
}

export const useInventoryContextMenu = ({ onEdit, onDelete }: IProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<IInventoryItem | null>(null);
  const history = useHistory();
  const dispatch = useDispatch();

  const canEditInventory = useSelector(selectIsSuperAdmin);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, item: IInventoryItem) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedItem(item);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedItem(null);
  }, []);

  const handleRowClick = useCallback(
    (clickedItem: IInventoryItem | null) => {
      if (!clickedItem) {
        return;
      }

      dispatch(addInventoryItemValue(clickedItem));

      const flowId = canEditInventory ? URL_VARS.EDIT : URL_VARS.VIEW;
      const url = routes.inventory.getInventoryUrl({ flowId, itemId: clickedItem.item_id });

      history.push(url);
    },
    [canEditInventory, dispatch, history],
  );

  const handleEditItem = useCallback(() => {
    if (!selectedItem) {
      return;
    }

    onEdit?.(selectedItem);
    handleMenuClose();
  }, [handleMenuClose, onEdit, selectedItem]);

  const handleDeleteItem = useCallback(() => {
    if (!selectedItem?.item_id) {
      return;
    }

    onDelete?.(selectedItem?.item_id);
    handleMenuClose();
  }, [handleMenuClose, selectedItem, onDelete]);

  return {
    menuAnchorEl,
    selectedItem,
    handleMenuOpen,
    handleMenuClose,
    handleRowClick,
    handleEditItem,
    handleDeleteItem,
    canEditInventory,
  };
};
